import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, Wallet, Layers, AlertTriangle,
  ClipboardList, Users, ShieldCheck, ListChecks, Upload, LogOut, Receipt, BookOpen, BarChart3, FolderKanban,
  Building2, History, PanelLeftClose, PanelLeftOpen, Menu, X
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { to: "/invoices", label: "Invoices", icon: FileText, module: "invoices" },
  { to: "/bulk-payment", label: "Bulk Payment", icon: Receipt, module: "payments" },
  { to: "/vouchers", label: "Voucher Register", icon: BookOpen, module: "payments" },
  { to: "/retentions", label: "Retentions", icon: Layers, module: "retentions" },
  { to: "/upload", label: "Excel Upload", icon: Upload, module: "excel_upload" },
  { to: "/reports", label: "Reports", icon: BarChart3, module: "reports" },
  { to: "/hr-compliance", label: "HR Compliance", icon: ClipboardList, module: "hr_compliance" },
  { to: "/projects", label: "Projects", icon: FolderKanban, module: "projects" },
  { to: "/parties", label: "Parties · TDS", icon: Building2, module: "parties" },
  { to: "/users", label: "Users", icon: Users, module: "users" },
  { to: "/roles", label: "Roles", icon: ShieldCheck, module: "roles" },
  { to: "/audit-log", label: "Audit Log", icon: History, module: "audit_log" },
  { to: "/status-options", label: "Status Options", icon: ListChecks, module: "status_options" },
];

export default function Layout() {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("voltledger_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("voltledger_sidebar_collapsed", collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);

  // Auto-close mobile drawer when route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // On mobile, force expanded view (collapse only matters on desktop)
  const sidebarWidth = collapsed ? "lg:w-16" : "lg:w-64";

  return (
    <div className="flex min-h-screen">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          data-testid="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          ${sidebarWidth}
          sidebar-dark flex flex-col shrink-0
          transition-[transform,width] duration-200 ease-out
          fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto
          w-64
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Mobile close button */}
        <button
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="lg:hidden absolute right-3 top-4 z-20 w-8 h-8 rounded-full bg-white border border-slate-300 shadow-md text-slate-600 hover:text-rose-700 flex items-center justify-center"
          data-testid="mobile-sidebar-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          data-testid="sidebar-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="hidden lg:flex absolute right-2 top-6 z-20 w-7 h-7 rounded-full bg-white border border-slate-300 shadow-md text-slate-600 hover:text-[#002FA7] hover:border-indigo-300 hover:bg-indigo-50 transition-colors items-center justify-center"
        >
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>

        <div className={`pt-6 pb-5 border-b border-slate-900/10 ${collapsed ? "lg:px-2" : "lg:px-5"} px-5`}>
          <div className="flex items-center justify-center">
            <img
              src="/brand/rcpl-logo.jpg?v=2"
              alt="Radiants Civiletric Pvt Ltd"
              className={`${collapsed ? "lg:h-9" : "lg:h-14"} h-14 w-auto object-contain transition-all`}
              style={{ mixBlendMode: "multiply" }}
            />
          </div>
          {/* Brand name — hidden when desktop-collapsed, always shown on mobile (since drawer is wide) */}
          <div className={`mt-2 text-center ${collapsed ? "lg:hidden" : ""}`}>
            <div className="text-[9px] font-semibold uppercase tracking-[0.28em] text-red-600">
              Radiants Civiletric
            </div>
            <div className="font-display text-lg font-bold tracking-tight text-slate-900 leading-tight mt-0.5">
              VoltLedger
            </div>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.filter(n => hasPerm(n.module)).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.module}`}
                title={collapsed ? n.label : undefined}
                className={({ isActive }) =>
                  `sidebar-link flex items-center gap-3 py-2.5 text-sm text-slate-700 px-6 ${collapsed ? "lg:px-0 lg:justify-center" : "lg:px-6"} ${
                    isActive ? "font-semibold" : "font-medium"
                  }`
                }
              >
                {({ isActive }) => (
                  <span data-active={isActive ? "true" : "false"} className={`flex items-center gap-3 w-full ${collapsed ? "lg:justify-center lg:gap-0" : ""}`}>
                    <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                    <span className={collapsed ? "lg:hidden" : ""}>{n.label}</span>
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-x-hidden flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-3 sm:px-4 lg:px-10 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
              className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 active:bg-slate-100 shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0 flex-wrap" data-testid="topbar-greeting">
              <span className="text-base sm:text-xl md:text-2xl lg:text-3xl font-display font-medium text-slate-500 whitespace-nowrap">Hello,</span>
              <span className="text-base sm:text-xl md:text-2xl lg:text-3xl font-display font-extrabold bg-gradient-to-r from-rose-600 via-red-600 to-amber-600 bg-clip-text text-transparent whitespace-nowrap tracking-tight">
                {user?.name?.split(" ")[0] || "there"}
              </span>
              <span className="hidden md:inline text-xl sm:text-2xl lg:text-3xl font-display font-medium text-slate-500">— Welcome to</span>
              <span className="hidden md:inline text-xl sm:text-2xl lg:text-3xl font-display font-extrabold bg-gradient-to-r from-[#002FA7] via-indigo-600 to-violet-600 bg-clip-text text-transparent tracking-tight whitespace-nowrap">
                Radiants VoltLedger
              </span>
              <span className="hidden lg:inline text-xl sm:text-2xl lg:text-3xl font-display font-medium text-slate-500">Dashboard</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="topbar-user">
            <div className="text-right hidden md:block leading-tight">
              <div className="text-sm font-semibold text-slate-900" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-red-700/80" data-testid="user-role">{user?.role_name}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-rose-700 text-white flex items-center justify-center text-xs font-semibold shadow-md shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600 border border-slate-200 hover:text-rose-700 hover:border-rose-300 hover:bg-rose-50 transition-colors rounded-none shrink-0"
              title="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>
        <div className="flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
